import { SideTree } from "./interfaces";

const getPositionIdentifier = (position = "") => position.split(" ").join("_").toLowerCase().trim();
const getRootIdentifier = (params: { commissionCycleId?: string; payPeriodId?: string; userId?: string }) =>
	`${params.commissionCycleId}_${params.payPeriodId}_${params.userId}`;

const getRootNode = (node: SideTree) => ({
	_id: `${node.commissionCycleId}_${node.payPeriodId}_${node.userId}_${getPositionIdentifier(node.position)}`,
	name: node.name,
	userId: node.userId,
	position: node.position,
	commissionCycleId: node.commissionCycleId,
	payPeriodId: node.payPeriodId,
	status: node.status,
	disabled: node.disabled,
	information: node.information,
});

const getUserRoots = (node: SideTree) => {
	const identifier = getRootIdentifier(node);

	if (!node?.list?.length) {
		return {
			identifier,
			values: [getRootNode(node)],
		};
	}

	return {
		identifier,
		values: node?.list?.map((item) => getRootNode(item)),
	};
};

const getUserNode = (node: SideTree) => ({
	_id: node.userId,
	userId: node.userId,
	name: node.fullName,
	fullName: node.fullName,
	status: node.status,
	list: [],
});

const getUniqueUsers = (tree: SideTree[] = []) => {
	const uniqueUsers = new Map();

	const traverse = (node: SideTree) => {
		if (node.userId) {
			uniqueUsers.set(node.userId, getUserNode(node));
		}
		if (node.list && !node.userId) {
			node.list.forEach((child) => traverse(child));
		}
	};

	tree.forEach((node) => traverse(node));

	return uniqueUsers;
};

const getMatchRootMap = (tree: SideTree[] = []) => {
	const rootMap = new Map();

	const traverse = (node: SideTree) => {
		if (node.userId) {
			const { identifier, values } = getUserRoots(node);
			rootMap.set(identifier, values);
		}
		if (node.list && !node.userId) {
			node.list.forEach((child) => traverse(child));
		}
	};

	tree.forEach((node) => traverse(node));

	return rootMap;
};

const getPeriodRoot = (node: SideTree): SideTree => {
	const [commissionCycleId, payPeriodId] = node._id.split("_");
	return {
		commissionCycleId,
		payPeriodId,
		_id: node._id,
		name: node.name,
		payPeriodProviders: node.payPeriodProviders ?? [],
		intervalCode: node.intervalCode,
		startDate: node.startDate,
		endDate: node.endDate,
		list: [],
	};
};

const getCyclesPeriods = (tree: SideTree[] = []): SideTree[] => {
	const traverse = (node: SideTree | SideTree[]): SideTree | SideTree[] => {
		if (!Array.isArray(node) && node?.intervalCode) {
			return getPeriodRoot(node);
		}
		if (!Array.isArray(node) && node?.list) {
			node["list"] = node.list.map((child: SideTree) => traverse(child) as SideTree);
			return node;
		}
		if (Array.isArray(node)) {
			return node.map((child: SideTree): SideTree => traverse(child) as SideTree);
		}
		return node;
	};

	return tree.map((cycle: SideTree) => ({
		_id: cycle._id,
		name: cycle.name,
		list: traverse(cycle.list) as SideTree[],
	}));
};

const matchUserPeriods = (period: SideTree, userId = "", matchRoots = new Map()) => {
	if (!period || !userId || !matchRoots.size) {
		return null;
	}
	const { commissionCycleId, payPeriodId } = period;
	const identifier = getRootIdentifier({ commissionCycleId, payPeriodId, userId });
	return matchRoots.get(identifier);
};

const setPeriodRoot = (period: SideTree, users = []) => {
	if (!users?.length) {
		return null;
	}

	const { userId, position, disabled = false, information = null } = users[0];
	const multiple = Boolean(users.length > 1);

	if ("list" in period) {
		// @ts-ignore
		delete (period as unknown).list;
	}

	if (multiple) {
		period["list"] = users.map((user: SideTree) => {
			user["name"] = user.position;
			delete user["information"];
			return user;
		});
	} else {
		period["position"] = position;
	}

	return {
		...period,
		userId,
		disabled,
		information,
	};
};

const isCycleDisabled = (list: SideTree[] = []): boolean => list.every((item: SideTree) => !item?.list?.length);

const fillUserCyclesTree = (userId = "", matchRoots = new Map(), cycles: SideTree[] = []) => {
	if (!userId || !matchRoots.size) {
		return [];
	}

	const traverse = (node: SideTree | SideTree[]) => {
		if (!Array.isArray(node) && node?.intervalCode) {
			return setPeriodRoot({ ...node }, matchUserPeriods(node, userId, matchRoots));
		}
		if (!Array.isArray(node) && node?.list) {
			node["list"] = traverse(node.list ? [...node.list] : []) as SideTree[];
		}
		if (Array.isArray(node)) {
			return node
				.map((child: SideTree): SideTree => traverse({ ...child, _id: `${child._id}_${userId}` }) as SideTree)
				.filter((item) => item);
		}
		return { ...node };
	};

	return cycles
		.map((item) => {
			const result = { ...item, _id: `${item._id}_${userId}` };
			const list = traverse([...result.list]);
			result["list"] = [...(list as SideTree[])].filter((node) => node).map((node) => ({ ...node, disabled: !node?.list?.length }));
			result["disabled"] = isCycleDisabled(list as SideTree[]);

			if (!result["list"]?.length) {
				return null;
			}
			return result;
		})
		.filter((node) => node);
};

const fillUsersTree = (users = new Map(), matchRoots = new Map(), cycles: SideTree[] = []) => {
	if (!users.size || !matchRoots.size) {
		return [];
	}

	const listUsers = Array.from(users.values());
	return listUsers
		.map((user) => {
			const { userId } = user;
			const list = fillUserCyclesTree(userId, matchRoots, [...cycles]);
			if (!list?.length) {
				return null;
			}
			user["list"] = [...list];
			return user;
		})
		.filter((node) => node);
};

const getStatusUsersMap = (users = new Map(), status = "ACTIVE") => {
	if (!users.size) {
		return new Map();
	}
	const statusUsers = new Map();
	const listUsers = Array.from(users.values());
	listUsers.forEach((user: { userId: string; status?: string }) => {
		if (user.status === status) {
			statusUsers.set(user.userId, user);
		}
	});
	return statusUsers;
};

const switchTreeOrdering = (tree: SideTree[] = []) => {
	const matchRoots = getMatchRootMap([...tree]);
	const uniqueUsers = getUniqueUsers([...tree]);
	const cycles = getCyclesPeriods([...tree]);

	// Separate by statuses
	const activeUsers = getStatusUsersMap(uniqueUsers);
	const inactiveUsers = getStatusUsersMap(uniqueUsers, "INACTIVE");

	const statuses = [
		{
			_id: "active",
			name: "Active",
			bold: true,
			list: fillUsersTree(activeUsers, matchRoots, cycles),
		},
		{
			_id: "inactive",
			name: "Inactive",
			bold: true,
			list: fillUsersTree(inactiveUsers, matchRoots, cycles),
		},
	];

	return statuses.filter((item) => item?.list?.length);
};

export { switchTreeOrdering };
